import React, { useState, useEffect } from "react";
import LinearProgress from "@mui/material/LinearProgress";
import { useTranslation } from "react-i18next";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

import Board from "./Board";
import { isBoardVisible, boardTypes } from "./moodboardSettings";
import getBoard from "./moodboardModel";
import useClasses from "../useClasses";
import useActiveContexts from "../Library/useActiveContexts";

const styles = (theme) => ({
  progress: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    "& > * + *": {
      marginTop: theme.spacing(2),
    },
  },
});

const CONTEXT_IDS_OCCURENCE_MOODBOARD = [19, 198];
const CONTEXT_IDS_NETWORK_BOARD = [19, 421];

export default function BoardList({
  mode,
  moodboard,
  quantitativeLabelsChecked,
  qualitativeLabelsChecked,
  rom,
}) {
  const { t } = useTranslation();
  const { contexts, isLoading: isContextLoading } = useActiveContexts();
  const classes = useClasses(styles);
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(false);

  // check if you have access to create the board (depending on which context you are in)
  const readAccess = ({ type }) => {
    switch (type) {
      case "occurrence":
        if (
          contexts.filter((context) =>
            CONTEXT_IDS_OCCURENCE_MOODBOARD.includes(context.contextId)
          ).length > 0
        ) {
          return true; // only access to the board if you are in the context
        }
        return false; // hide the board by default
      case "network":
        if (
          contexts.filter((context) =>
            CONTEXT_IDS_NETWORK_BOARD.includes(context.contextId)
          ).length > 0
        ) {
          return true;
        }
        return false; // hide the board by default
      default:
        return true;
    }
  };

  useEffect(() => {
    if (!isContextLoading) {
      setLoading(true);
      const timer = setTimeout(() => {
        const newBoards =
          mode === "explore"
            ? Object.values(boardTypes)
                .map((type) => getBoard({ ...moodboard, type }))
                .filter(
                  ({ type }) =>
                    isBoardVisible({
                      type,
                      quantitativeLabelsChecked,
                      qualitativeLabelsChecked,
                      rom,
                    }) && readAccess({ type })
                )
            : [getBoard(moodboard)];
        const renderBoards = newBoards.map((board, index) => (
          <Grid key={board.type} item xs={12} md={board.size}>
            <Board variant={"analyser"} rom={rom} item={board} />
          </Grid>
        ));
        setBoards(renderBoards);
        setLoading(false);
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [moodboard, rom, quantitativeLabelsChecked, isContextLoading]);

  return (
    <div>
      {loading ? (
        <div className={classes.progress}>
          <LinearProgress variant="indeterminate" />{" "}
        </div>
      ) : null}
      {boards.length === 0 ? (
        <Typography variant="subtitle1">
          <span style={{ fontSize: 32 }}>&#8592;</span>{" "}
          {t("moodboards.subtitle")}
        </Typography>
      ) : null}
      <Grid
        container
        direction="row"
        justifyContent="flex-start"
        alignItems="flex-start"
        spacing={3}
      >
        {boards.map((board) => board)}
      </Grid>
    </div>
  );
}
